/** biome-ignore-all lint/suspicious/noConsole: We dont have logger here */
import type { ValidationError } from '@tanstack/react-form'
import Color from 'color'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { produce, setAutoFreeze } from 'immer'
import type { KeyboardEvent } from 'react'
import { type ApiResponse, TimeUnits } from '../types'

dayjs.extend(duration)

export function convertToMs(time: number, unit: TimeUnits) {
  switch (unit) {
    case TimeUnits.Minutes:
      return time * 60000
    case TimeUnits.Hours:
      return time * 3600000
    case TimeUnits.Days:
      return time * 86400000
    default:
      return time
  }
}

export const pluralize = (count: number, noun: string, suffix = 's') =>
  `${count} ${noun}${count !== 1 ? suffix : ''}`

// Snippet taken from https://stackoverflow.com/a/33076482
// Get the initials of a name
export const getInitials = (name = 'xx') => {
  const rgx = /(\p{L}{1})\p{L}+/gu
  const initials = [...name.matchAll(rgx)]
  return ((initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')).toUpperCase()
}

// Typeguard to check if element is of type HTMLElement
export const isHTMLElement = (element: unknown): element is HTMLElement =>
  element instanceof HTMLElement

// Internal Zod error handling to hide error messages
const zodInternalErrorString = 'ZOD_INTERNAL_ERROR:'
export const internalZodMessage = (text: string) => `${zodInternalErrorString} ${text}`
export const isInternalZodMessage = (text: ValidationError) =>
  typeof text === 'string' && text.startsWith(zodInternalErrorString)
export const cleanInternalZodMessage = (text: ValidationError) =>
  typeof text === 'string' && text.replace(zodInternalErrorString, '')

const inputTags = ['INPUT', 'SELECT', 'TEXTAREA']

export function isInputDOMNode(event: KeyboardEvent<HTMLSpanElement>) {
  // Determine the target element of the event.
  // - `composedPath()` is preferred to get the full path of the event through Shadow DOM.
  // - Fall back to `event.target` if `composedPath` isn't available.
  const target = ((event as unknown as Event).composedPath?.()[0] || event.target) as HTMLElement

  // Check if the target is one of our known input types
  const isStandardInput = inputTags.includes(target?.nodeName)

  // Check if it's a contenteditable element or has a parent with the "nokey" class
  const isSpecialInput = target?.hasAttribute('contenteditable') || target?.closest('.nokey')

  return isStandardInput || isSpecialInput
}

export const generateColorPalette = (primaryColor: string, isHSL = false) => {
  const hexToHSL = (hex: string): string => {
    // Remove the hash if it exists
    let hexColor = hex
    hexColor = hexColor.replace(/^#/, '')

    // Parse the hex values
    const r = parseInt(hexColor.slice(0, 2), 16) / 255
    const g = parseInt(hexColor.slice(2, 4), 16) / 255
    const b = parseInt(hexColor.slice(4, 6), 16) / 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h = 0
    let s: number
    let l = (max + min) / 2

    if (max === min) {
      h = 0
      s = 0 // achromatic
    } else {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0)
          break
        case g:
          h = (b - r) / d + 2
          break
        case b:
          h = (r - g) / d + 4
          break
        default:
          break
      }
      h /= 6
    }

    // Convert to degrees, percentage, and percentage
    h = Math.round(h * 360)
    s = Math.round(s * 100)
    l = Math.round(l * 100)

    return `${h}, ${s}%, ${l}%`
  }

  const generateTintColor = (hexColor: string, tint: number) => {
    try {
      return Color('#ffffff')
        .mix(Color(hexColor), 1 - tint)
        .hex()
    } catch (_err) {
      return hexColor
    }
  }

  const generateShadeColor = (hexColor: string, shade: number) => {
    try {
      return Color(hexColor).darken(shade).hex()
    } catch (_err) {
      return hexColor
    }
  }

  const getFontColor = (bgColor: string) =>
    Color(bgColor).isLight() ? generateShadeColor(bgColor, 0.7) : '#ffffff'

  const fontColor = getFontColor(primaryColor)
  const backgroundColor = primaryColor
  const primaryHoverColor = generateTintColor(backgroundColor, 0.2)
  const iconColor = fontColor
  const menuButtonBck = generateTintColor(backgroundColor, 0.9)
  const menuButtonHoverBck = generateTintColor(backgroundColor, 0.7)
  const menuButtonTextColor = Color(backgroundColor).isLight()
    ? generateShadeColor(backgroundColor, 0.5)
    : backgroundColor

  if (isHSL) {
    return {
      primary: hexToHSL(backgroundColor),
      primaryHover: hexToHSL(primaryHoverColor),
      secondary: hexToHSL(menuButtonBck),
      secondaryHover: hexToHSL(menuButtonHoverBck),
      secondaryTextColor: hexToHSL(menuButtonTextColor),
      iconColor: hexToHSL(iconColor),
      fontColor: hexToHSL(fontColor),
      isLight: Color(primaryColor).isLight(),
    }
  }

  return {
    primary: backgroundColor,
    primaryHover: primaryHoverColor,
    secondary: menuButtonBck,
    secondaryHover: menuButtonHoverBck,
    secondaryTextColor: menuButtonTextColor,
    iconColor,
    fontColor,
    isLight: Color(primaryColor).isLight(),
  }
}

export const downloadFile = async (fileUrl: string, fileName: string, blob?: Blob) => {
  // blob: URLs are already in memory; fetching them again is blocked by CSP connect-src
  if (!blob && fileUrl.startsWith('blob:')) {
    const anchor = Object.assign(document.createElement('a'), { href: fileUrl, download: fileName || 'downloaded-file' })
    anchor.click()
    return
  }
  try {
    let downloadBlob: Blob

    if (blob) {
      // Use provided blob
      downloadBlob = blob
    } else {
      // Fetch blob from URL
      const response = await fetch(fileUrl)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      downloadBlob = await response.blob()
      if (downloadBlob.size === 0) {
        throw new Error('Downloaded file is empty')
      }
    }

    const url = window.URL.createObjectURL(downloadBlob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = fileName || 'downloaded-file'
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()

    setTimeout(() => {
      document.body.removeChild(anchor)
      window.URL.revokeObjectURL(url)
    }, 100)
  } catch (error) {
    console.error('Download failed:', error)
    throw error
  }
}

export const REGEX = {
  alphanumeric: /^[\w\d\s]+$/g,
  url: /(?:(?:https?|ftp):\/\/)?(?:www\.)?[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+(?:\/[^?\s]*)?(?:\?[^#\s]*)?(?:#[^\s]*)?/g,
  email: /\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/g,
  domain: /^https?:\/\/[-\w\d]+(?:\.[-\w\d]+)*(?:\/[-\w\d.]+)*(?:\.\w+)?$/,
}

export const isValidDomain = (url: string) => String(url).match(REGEX.domain)

// Merge with array support
export const mergeWithReplaceArray = (a: unknown, b: unknown) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return b
  }
  return undefined
}

export const truncateString = (text: string, charLimit = 300, wordLimit = 50) => {
  // Split text into words
  const words = text.split(' ')
  let truncatedText = ''

  // Iterate through words and add them to the truncated text
  for (let i = 0; i < words.length && i < wordLimit; i++) {
    // Check if adding the current word would exceed the character limit
    if (truncatedText.length + words[i].length + 1 > charLimit) {
      break // Stop if character limit is reached
    }
    // Add word to the truncated text
    truncatedText += (i === 0 ? '' : ' ') + words[i]
  }

  // Append ellipsis if the text is truncated
  return truncatedText.length < text.length ? `${truncatedText}...` : truncatedText
}

export const getCurrentTimestamp = () => {
  try {
    const exactMicroseconds =
      BigInt(Date.now()) * 10000n + BigInt(Math.floor((performance.now() % 1) * 10000))
    return exactMicroseconds.toString()
  } catch (_error) {
    return `${Date.now() * 10000}`
  }
}

/**
 * Executes Immer's produce function while temporarily disabling auto-freezing.
 * This is useful in scenarios where auto-freezing might interfere with other libraries or state management.
 *
 * @template T The type of the base state and the resulting state.
 * @param {T} baseState The initial state to produce the next state from.
 * @param {(draft: T) => void | T} recipe A function that receives a draft of the base state and can modify it to produce the next state. It can either mutate the draft or return a new state.
 * @returns {T} The next state produced by the recipe.
 */
export function produceWithAutoFreezeDisabled<T extends object>(
  baseState: T,
  recipe: (draft: T) => undefined | T,
): T {
  setAutoFreeze(false)
  const nextState = produce(baseState, recipe)
  setAutoFreeze(true)
  return nextState
}

export function toApiResponse<T>({
  data,
  api_id = '',
  message = '',
  totalCount,
  errors,
}: {
  data: T
  api_id?: string
  message?: string
  totalCount?: number
  errors?: ApiResponse<T>['errors']
}): ApiResponse<T> & { totalCount?: number } {
  return {
    api_id,
    data,
    message,
    errors: errors || {
      global_error: '',
      error_data: { description: '', comments: '' },
    },
    ...(typeof totalCount === 'number' ? { totalCount } : {}),
  }
}

/**
 * Capitalizes the first letter of each word in a string and lowercases the rest,
 * while preserving all original punctuation and spacing.
 *
 * Example:
 *   capitalizeEachWordWithPunctuationPreserved("hELLO, wORLD! how-are_you?") // "Hello, World! How-Are_You?"
 */
export function capitalizeEachWordWithPunctuationPreserved(str: string) {
  return str
    .split(/(\W+)/) // split but keep punctuation (commas, spaces, dashes, etc.)
    .map((part: string) => {
      if (/^[a-zA-Z]/.test(part)) {
        const lower = part.toLowerCase()
        return lower.charAt(0).toUpperCase() + lower.slice(1)
      }
      return part
    })
    .join('')
}

// Duration formatting utility for audio/call durations
export const formatDurationForDisplay = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00'
  const durationObj = dayjs.duration(seconds, 'seconds')
  const hours = Math.floor(durationObj.asHours())

  if (hours > 0) {
    return durationObj.format('HH:mm:ss')
  }
  return durationObj.format('mm:ss')
}

// Human-readable duration formatting (e.g., "2h 30m 15s", "45s")
export const formatDuration = (seconds: number | null) => {
  if (seconds === null || seconds === undefined) return '-'

  const durationObj = dayjs.duration(seconds, 'seconds')

  // For very short durations (< 1 second), show decimal seconds
  if (seconds < 1) {
    return `${seconds.toFixed(2)}s`
  }

  // Use dayjs humanize for longer durations, but customize the format.
  // asHours() (total), not hours() (component, 0-23): the component silently drops whole
  // days, so period totals (e.g. Agent Availability's Active Time) rendered 160h as "16h".
  // Output is identical for durations under 24h.
  const hours = Math.floor(durationObj.asHours())
  const minutes = durationObj.minutes()
  const secs = durationObj.seconds()

  const parts = []
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`)

  return parts.join(' ')
}

// Avatar color utility - generates gradient background from string hash
export const getAvatarColor = (str: string) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = Math.abs(hash) % 360
  const hueStart = (hue - 40) % 360
  const hueEnd = (hue + 40) % 360
  return {
    bg: `linear-gradient(to bottom right, hsla(${hueStart}, 65%, 40%, 0.4), hsla(${hueEnd}, 65%, 40%, 1))`,
    text: 'hsla(0, 0%, 100%, 1)',
  }
}

// Re-export conversation navigation utilities
export { getFirstAvailableConversationRoute } from './conversation-navigation'
// Re-export country constants
export { buildContactoCountryData, PRIORITY_COUNTRIES } from './country-constants'
// Re-export navigation utilities
export { safeNavigateBack } from './navigation-utils'
